import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { TodaysEscapesScheduleGenerationSegment } from '~/queries/customerCommunication/requestPostTodaysEscapesScheduleGeneration';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import { useRegenerateTailDeals } from '~/hooks/apis/customer-communication/useRegenerateTailDeals';
import useHeroPlannerCountryGroups from '~/hooks/customerCommunication/useHeroPlannerCountryGroups';

import { Dayjs, getDayJs } from '~/services/TimeService';

import { EmptyArray } from '~/utils/arrayUtils';
import { isRequestRejected, isRequestUnresolved } from '~/utils/requestUtils';

import {
  LIFESTYLE_GENERATION_SEGMENTS,
  requiresHeroPlannerGenerationSegment,
} from '../utils/heroPlannerGenerationUtils';

const SEND_DATE_FORMAT = 'YYYY-MM-DD';

interface Props {
  open: boolean;
  onClose: () => void;
  brand: string;
  /**
   * YYYY-MM-DD; a string so a re-render with the same date doesn't reset the form
   */
  defaultSendDate: string;
  defaultCountryGroupId?: string;
}

function RegenerateTailDealsDialog({ open, onClose, brand, defaultSendDate, defaultCountryGroupId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { countryGroupsReq } = useHeroPlannerCountryGroups(brand);
  const countryGroups = countryGroupsReq?.result ?? EmptyArray;
  const isCountryGroupsUnavailable = isRequestUnresolved(countryGroupsReq) || isRequestRejected(countryGroupsReq);
  const regenerateTailDeals = useRegenerateTailDeals();

  const [sendDate, setSendDate] = useState<Dayjs | null>(() => getDayJs(defaultSendDate));
  const [countryGroupId, setCountryGroupId] = useState(defaultCountryGroupId ?? '');
  const [segment, setSegment] = useState<TodaysEscapesScheduleGenerationSegment | ''>('');

  // start from whatever the schedules list is showing each time the dialog opens
  useEffect(() => {
    if (open) {
      setSendDate(getDayJs(defaultSendDate));
      setCountryGroupId(defaultCountryGroupId ?? '');
      setSegment('');
    }
  }, [open, defaultSendDate, defaultCountryGroupId]);

  const isSegmentRequired = requiresHeroPlannerGenerationSegment(brand);
  const countryGroupName = countryGroups.find((countryGroup) => countryGroup.id === countryGroupId)?.name;
  const canSubmit =
    !!sendDate?.isValid() && !!countryGroupId && (!isSegmentRequired || !!segment) && !regenerateTailDeals.isPending;

  const handleRegenerate = async () => {
    if (!canSubmit) {
      return;
    }
    try {
      await regenerateTailDeals.mutateAsync({
        brand,
        countryGroup: countryGroupId,
        sendDate: sendDate.format(SEND_DATE_FORMAT),
        segment: segment || undefined,
      });
      enqueueSnackbar(
        `Tail deal generation queued for ${countryGroupName ?? countryGroupId} on ${sendDate.format('D MMM YYYY')}. Use Debug to watch the schedules come back.`,
        { variant: 'success' },
      );
      onClose();
    } catch (error) {
      enqueueSnackbar(`Couldn't regenerate tail deals: ${error instanceof Error ? error.message : String(error)}`, {
        variant: 'error',
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={regenerateTailDeals.isPending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="regenerate-tail-deals-title"
    >
      <DialogTitle id="regenerate-tail-deals-title">Regenerate tail deals</DialogTitle>
      <DialogContent>
        <Stack gap={2.5} pt={1}>
          <DialogContentText>
            Rebuilds the tail deals for every country in the country group, using the current hero planner rows.
          </DialogContentText>
          <Alert severity="warning">
            Existing schedules for this date are marked as regenerating straight away and can't be sent until generation
            finishes. Avoid running this close to the send time.
          </Alert>
          <DatePicker
            label="Send date"
            value={sendDate}
            format="DD/MM/YYYY"
            onChange={setSendDate}
            slotProps={{ textField: { required: true, fullWidth: true } }}
          />
          <TextField
            select
            required
            fullWidth
            label="Country group"
            value={countryGroupId}
            onChange={(event) => setCountryGroupId(event.target.value)}
            disabled={isCountryGroupsUnavailable}
            error={isRequestRejected(countryGroupsReq)}
            helperText={
              isRequestRejected(countryGroupsReq)
                ? `Couldn't load country groups: ${countryGroupsReq.error}`
                : isRequestUnresolved(countryGroupsReq)
                  ? 'Loading country groups…'
                  : undefined
            }
          >
            {countryGroups.map((countryGroup) => (
              <MenuItem key={countryGroup.id} value={countryGroup.id}>
                {countryGroup.name}
              </MenuItem>
            ))}
          </TextField>
          {isSegmentRequired && (
            <TextField
              select
              required
              fullWidth
              label="Segment"
              value={segment}
              onChange={(event) => setSegment(event.target.value as TodaysEscapesScheduleGenerationSegment)}
              helperText="Travel and experience sends are generated separately"
            >
              {LIFESTYLE_GENERATION_SEGMENTS.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={regenerateTailDeals.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleRegenerate} disabled={!canSubmit}>
          {regenerateTailDeals.isPending ? 'Regenerating…' : 'Regenerate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RegenerateTailDealsDialog;
